/**
 * Idle sign-out: the session ends after a stretch without user input in any tab of the sign-in. The clock is wall
 * time, so time a tab spends hidden, asleep or in the background counts the same as time on screen; a hidden tab whose
 * timers the browser has throttled is checked again the moment it becomes visible. The time of the latest input is
 * also kept in sessionStorage, so a reload, a discarded tab brought back, a reopened closed tab or a restored browser
 * session continues the same clock instead of starting a fresh one, and it is shared with the sign-in's other tabs
 * (tabs.ts), so a tab in the background never signs out the tab in use.
 *
 * The timeout is one constant for now. Per-clinic configurable timeouts are a later feature (shared-iPad sign-in
 * brief, items 8 and 9) and are not built here.
 */
import { randomToken } from "./pkce";
import type { TabChannel } from "./tabs";

/** Sign out after this long without user activity (pointer, keyboard, touch, scroll). */
export const IDLE_SIGN_OUT_MS = 15 * 60_000;

/** The warning, with its "Stay signed in" action, shows this long before the idle sign-out. */
export const IDLE_WARNING_MS = 60_000;

/** DOM events that count as user activity. Listened for on the document in the capture phase. */
export const IDLE_ACTIVITY_EVENTS = ["pointerdown", "pointermove", "keydown", "touchstart", "wheel", "scroll"] as const;

/** sessionStorage key marking that the last sign-out in this tab was an idle one, so the signed-out page can say so. */
export const IDLE_SIGNED_OUT_KEY = "a123.auth.idle-signed-out";

export type IdlePhase = "active" | "warning" | "expired";

export interface IdleState {
  phase: IdlePhase;
  /** Epoch milliseconds at which the session ends for idleness. */
  deadline: number;
  /** Milliseconds until the deadline, never negative. */
  msLeft: number;
}

export interface IdleTiming {
  timeoutMs?: number;
  warningMs?: number;
}

/**
 * Where an idle clock stands. `holdUntil` (epoch ms) defers the deadline while the app has a reason to keep the
 * session, such as a test timer this tab started that is still running; the warning still comes first.
 */
export function idleState(input: { lastActivity: number; now: number; holdUntil?: number | null } & IdleTiming): IdleState {
  const timeoutMs = input.timeoutMs ?? IDLE_SIGN_OUT_MS;
  const warningMs = input.warningMs ?? IDLE_WARNING_MS;
  const deadline = Math.max(input.lastActivity + timeoutMs, input.holdUntil ?? Number.NEGATIVE_INFINITY);
  const msLeft = Math.max(0, deadline - input.now);
  const phase: IdlePhase = msLeft <= 0 ? "expired" : msLeft <= warningMs ? "warning" : "active";
  return { phase, deadline, msLeft };
}

export interface IdleMonitor {
  /**
   * Records user activity now. Ignored during the warning, where only `stay` counts (so a press on the warning's own
   * buttons does not dismiss it under the pointer), and once expired: an expired session is never revived.
   */
  activity(): void;
  /** The warning's "Stay signed in": resets the clock unless the session has already expired. */
  stay(): void;
  /**
   * Input another tab of the sign-in saw at `at` (epoch ms). It counts during the warning too, since someone is using
   * the sign-in, and it can still revive a clock whose deadline passed while the expiry is settling.
   */
  observe(at: number): void;
  /** Re-evaluates the clock (on a tick, on becoming visible, on focus). */
  check(): IdleState;
  setHoldUntil(holdUntil: number | null): void;
  state(): IdleState;
  /** Epoch ms of the latest activity the clock counts. */
  lastActivityAt(): number;
}

/**
 * The clock without the DOM, so it can be driven by tests with an injected `now`. `onChange` fires when the phase
 * changes and, during the warning, when the whole seconds left change; `onExpire` fires exactly once.
 *
 * `lastActivity` seeds the clock (from storage after a reload); without it the clock starts now. With `settleMs`, a
 * deadline that passes does not end the session at once: the phase is "expired" (the screen is hidden), `onSettle`
 * asks the other tabs for newer input, and `onExpire` fires on the first check at least `settleMs` later unless
 * `observe` or a hold moved the deadline in the meantime.
 */
export function createIdleMonitor(options: { now?: () => number; lastActivity?: number; onChange?: (state: IdleState) => void; onExpire: () => void; onSettle?: () => void; settleMs?: number; holdUntil?: number | null } & IdleTiming): IdleMonitor {
  const now = options.now ?? (() => Date.now());
  const settleMs = options.settleMs ?? 0;
  let lastActivity = Math.min(options.lastActivity ?? now(), now());
  let holdUntil = options.holdUntil ?? null;
  let expired = false;
  let settlingSince: number | null = null;
  let last: IdleState = compute();

  function compute(): IdleState {
    return idleState({ lastActivity, now: now(), holdUntil, timeoutMs: options.timeoutMs, warningMs: options.warningMs });
  }

  function evaluate(): IdleState {
    if (expired) return last;
    const next = compute();
    // While active only the phase matters to a screen; during the warning the seconds left are shown.
    const changed = next.phase !== last.phase || (next.phase === "warning" && Math.ceil(next.msLeft / 1000) !== Math.ceil(last.msLeft / 1000));
    last = next;
    if (next.phase !== "expired") settlingSince = null;
    else if (settleMs > 0 && settlingSince === null) {
      settlingSince = now();
      if (changed) options.onChange?.(next);
      options.onSettle?.();
      return last;
    }
    if (changed) options.onChange?.(next);
    if (next.phase === "expired" && (settlingSince === null || now() - settlingSince >= settleMs)) {
      expired = true;
      options.onExpire();
    }
    return last;
  }

  function reset(allowDuringWarning: boolean) {
    if (expired) return;
    const phase = compute().phase;
    // A tab that slept past its deadline must not be revived by the first touch after waking.
    if (phase === "expired" || (phase === "warning" && !allowDuringWarning)) {
      evaluate();
      return;
    }
    lastActivity = now();
    evaluate();
  }

  return {
    activity: () => reset(false),
    stay: () => reset(true),
    observe(at) {
      if (expired) return;
      lastActivity = Math.max(lastActivity, Math.min(at, now()));
      evaluate();
    },
    check: evaluate,
    setHoldUntil(next) {
      holdUntil = next;
      evaluate();
    },
    state: () => last,
    lastActivityAt: () => lastActivity,
  };
}

/** Marks this tab's next signed-out page as the result of an idle sign-out. Storage failures are ignored. */
export function markIdleSignOut(storage: Pick<Storage, "setItem"> | undefined = typeof sessionStorage !== "undefined" ? sessionStorage : undefined) {
  try {
    storage?.setItem(IDLE_SIGNED_OUT_KEY, "1");
  } catch {
    /* storage may be unavailable */
  }
}

/** Whether this tab's last sign-out was an idle one. Read during render; clear it with `clearIdleSignOut` in an effect. */
export function wasIdleSignOut(storage: Pick<Storage, "getItem"> | undefined = typeof sessionStorage !== "undefined" ? sessionStorage : undefined): boolean {
  try {
    return storage?.getItem(IDLE_SIGNED_OUT_KEY) === "1";
  } catch {
    return false;
  }
}

export function clearIdleSignOut(storage: Pick<Storage, "removeItem"> | undefined = typeof sessionStorage !== "undefined" ? sessionStorage : undefined) {
  try {
    storage?.removeItem(IDLE_SIGNED_OUT_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * sessionStorage key of this tab's idle record: the sign-in's tab group id and the time of the latest input, nothing
 * else. It is copied with the session when a tab is duplicated or restored, which is what makes the clock survive
 * those and what tells a sign-in's tabs apart from others (tabs.ts).
 */
export const IDLE_RECORD_KEY = "a123.auth.idle";

/** A tab writes its own input to storage and to the other tabs at most this often (the first input after a pause at once). */
export const IDLE_SHARE_EVERY_MS = 10_000;

/** How long an idle sign-out waits for the other tabs to report newer input before it goes ahead. */
export const IDLE_SETTLE_MS = 300;

export interface IdleRecord {
  group: string;
  /** Epoch milliseconds. */
  lastActivity: number;
}

type IdleStorage = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const sessionStore = (): IdleStorage | undefined => (typeof sessionStorage !== "undefined" ? sessionStorage : undefined);

export function readIdleRecord(storage: Pick<Storage, "getItem"> | undefined = sessionStore()): IdleRecord | null {
  try {
    const raw = storage?.getItem(IDLE_RECORD_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<IdleRecord>;
    return typeof v.group === "string" && v.group && typeof v.lastActivity === "number" && Number.isFinite(v.lastActivity) ? { group: v.group, lastActivity: v.lastActivity } : null;
  } catch {
    return null;
  }
}

function writeIdleRecord(record: IdleRecord, storage: Pick<Storage, "setItem"> | undefined) {
  try {
    storage?.setItem(IDLE_RECORD_KEY, JSON.stringify(record));
  } catch {
    /* storage may be unavailable: the clock then lasts for this page only */
  }
}

/** A new sign-in: a new tab group, and the clock starting now. Call it when the sign-in completes. */
export function startIdleRecord(now = Date.now(), storage: Pick<Storage, "setItem"> | undefined = sessionStore()): IdleRecord {
  const record = { group: randomToken(16), lastActivity: now };
  writeIdleRecord(record, storage);
  return record;
}

/** Called at sign-out, so the next sign-in in this tab starts a new group and a fresh clock. */
export function clearIdleRecord(storage: Pick<Storage, "removeItem"> | undefined = sessionStore()) {
  try {
    storage?.removeItem(IDLE_RECORD_KEY);
  } catch {
    /* ignore */
  }
}

/** This tab's group, or null when signed out (or without storage). */
export function idleGroup(storage: Pick<Storage, "getItem"> | undefined = sessionStore()): string | null {
  return readIdleRecord(storage)?.group ?? null;
}

/**
 * Where the stored clock stands, for a screen deciding whether to show protected content before anything else has
 * run (the first render after a reload or a restore). Null when there is no stored clock.
 */
export function storedIdleState(options: { now?: number; storage?: Pick<Storage, "getItem"> } & IdleTiming = {}): IdleState | null {
  const record = readIdleRecord(options.storage ?? sessionStore());
  return record ? idleState({ lastActivity: record.lastActivity, now: options.now ?? Date.now(), timeoutMs: options.timeoutMs, warningMs: options.warningMs }) : null;
}

export interface IdleSession {
  /** User input in this tab. */
  activity(): void;
  /** The warning's "Stay signed in". */
  stay(): void;
  check(): IdleState;
  /** This tab's own hold (work in progress), shared with the sign-in's other tabs. */
  setHold(until: number | null): void;
  state(): IdleState;
  close(): void;
}

/**
 * One tab's idle clock, kept in step with storage and with the sign-in's other tabs:
 * - it starts from the stored time of the latest input (a fresh clock only when there is none);
 * - this tab's input is stored and sent to the other tabs at most every `IDLE_SHARE_EVERY_MS`, "Stay signed in" at once;
 * - input the other tabs report counts here, so the tab in use keeps every tab of the sign-in signed in;
 * - each tab's hold (a running test timer, say) holds them all;
 * - before an idle sign-out the tab asks the others for newer input and waits `IDLE_SETTLE_MS`, so a tab that was
 *   asleep, frozen or reloaded from an old record does not sign out (and revoke the token of) a tab still in use.
 */
export function createIdleSession(options: { now?: () => number; storage?: IdleStorage; channel?: TabChannel | null; hold?: number | null; onChange?: (state: IdleState) => void; onExpire: () => void; shareEveryMs?: number; settleMs?: number } & IdleTiming): IdleSession {
  const now = options.now ?? (() => Date.now());
  const storage = options.storage ?? sessionStore();
  const channel = options.channel ?? null;
  const shareEvery = options.shareEveryMs ?? IDLE_SHARE_EVERY_MS;
  const record = readIdleRecord(storage) ?? startIdleRecord(now(), storage);
  let localHold = options.hold ?? null;
  const remoteHolds = new Map<string, number>();
  const hold = () => [...remoteHolds.values()].reduce<number | null>((h, u) => (h === null ? u : Math.max(h, u)), localHold);

  const monitor = createIdleMonitor({
    now,
    lastActivity: record.lastActivity,
    holdUntil: hold(),
    timeoutMs: options.timeoutMs,
    warningMs: options.warningMs,
    settleMs: channel ? (options.settleMs ?? IDLE_SETTLE_MS) : 0,
    onSettle: () => channel?.post({ kind: "hello" }),
    onChange: options.onChange,
    onExpire: options.onExpire,
  });

  // The monitor's own time, not the stored one: a stored time ahead of the clock (the clock was set back) is not kept.
  let shared = monitor.lastActivityAt();
  const persist = () => {
    // Only while signed in: the sign-out removes the record, and a late event must not bring it back.
    const current = readIdleRecord(storage);
    if (current) writeIdleRecord({ group: current.group, lastActivity: monitor.lastActivityAt() }, storage);
  };
  const share = (force: boolean) => {
    const at = monitor.lastActivityAt();
    if (at <= shared && !force) return;
    if (!force && at - shared < shareEvery) return;
    shared = at;
    persist();
    channel?.post({ kind: "activity", at });
  };

  const off = channel?.subscribe((m) => {
    if (m.kind === "activity") {
      monitor.observe(m.at);
      persist();
    } else if (m.kind === "hold") {
      if (m.until === null) remoteHolds.delete(m.tab);
      else remoteHolds.set(m.tab, m.until);
      monitor.setHoldUntil(hold());
    } else if (m.kind === "hello") {
      channel.post({ kind: "activity", at: monitor.lastActivityAt() });
      if (localHold !== null) channel.post({ kind: "hold", until: localHold });
    }
  });
  // Learn the other tabs' latest input and holds now, then see whether the stored clock has already run out.
  channel?.post({ kind: "hello" });
  if (localHold !== null) channel?.post({ kind: "hold", until: localHold });
  monitor.check();

  return {
    activity() {
      monitor.activity();
      share(false);
    },
    stay() {
      monitor.stay();
      share(true);
    },
    check: () => monitor.check(),
    setHold(until) {
      if (until === localHold) return;
      localHold = until;
      monitor.setHoldUntil(hold());
      channel?.post({ kind: "hold", until });
    },
    state: () => monitor.state(),
    close() {
      off?.();
      if (localHold !== null) channel?.post({ kind: "hold", until: null });
    },
  };
}
